import React from 'react';
import { FileText } from 'lucide-react';
import { StaticPage } from './ObjectRenderer';
import { SpreadSheet, openingSheet, showsAsSpread } from '../../lib/sheetRender';

/** Size of a page (px) scaled to fit inside a box, keeping its aspect ratio. */
export const fitPage = (pw, ph, maxW, maxH) => {
  const scale = Math.min(maxW / (pw || 816), maxH / (ph || 1056));
  return { width: Math.max(1, Math.round((pw || 816) * scale)), height: Math.max(1, Math.round((ph || 1056) * scale)), scale };
};

/**
 * Picture of a saved publication for the Home / Open lists: the stored snapshot when there is one (captured on every
 * cloud save), otherwise a live render from the publication data itself (the list carries pages and masters), and a
 * document glyph only when there is no page at all. Always letter-boxed to the real aspect ratio.
 *
 * A publication kept in Two-Page Spread view shows its opening spread — page 1 on the right, the empty half outlined —
 * so a booklet looks like a booklet in the lists; everything else shows page 1.
 */
export const PublicationPreview = ({ project, maxW = 96, maxH = 120, className = '', testId }) => {
  const page = project?.pages?.[0];
  const spread = showsAsSpread(project);
  const sheet = spread ? openingSheet(project) : null;
  const { width, height, scale } = fitPage(sheet ? sheet.width : page?.width, sheet ? sheet.height : page?.height, maxW, maxH);
  const frame = `bg-white shadow-sm ring-1 ring-black/10 overflow-hidden shrink-0 ${className}`;
  const alt = `${project?.name || 'Publication'} — ${spread ? 'opening spread' : 'page 1'}`;
  if (project?.thumbnail) {
    return (
      <img src={project.thumbnail} alt={alt} width={width} height={height} className={`${frame} object-contain`} style={{ width, height }} loading="lazy" decoding="async" data-layout={spread ? 'spread' : 'single'} data-testid={testId || `publication-thumb-${project.id}`} />
    );
  }
  if (sheet) {
    return (
      <div className={frame} style={{ width, height }} aria-label={`${alt} preview`} data-layout="spread" data-testid={testId || `publication-live-preview-${project.id}`}>
        <SpreadSheet sheet={sheet} masters={project.masters || {}} textFlow={project.textFlow || project.text_flow || {}} scale={scale} emptySlot />
      </div>
    );
  }
  if (page) {
    return (
      <div className={frame} style={{ width, height }} aria-label={`${alt} preview`} data-layout="single" data-testid={testId || `publication-live-preview-${project.id}`}>
        <StaticPage page={page} master={project.masters?.[page.masterId || 'default']} scale={scale} />
      </div>
    );
  }
  return (
    <div className={`${frame} flex items-center justify-center text-muted-foreground`} style={{ width, height }} data-testid={testId || `publication-placeholder-${project?.id}`}>
      <FileText className="h-5 w-5" aria-hidden="true" />
    </div>
  );
};
